
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import { useParams } from "react-router-dom";
import "../../../assets/css/FaqSection.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const FAQS_PER_PAGE = 10;

function FaqSection() {
  const { section, id } = useParams();

  const [activeIndex, setActiveIndex] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchFaqData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);

    try {
      await axios.get("sanctum/csrf-cookie");

      const response = await axios.get(
        `${BASE_URL}faqdata?visibility[eq]=publish`
      );

      if (response?.data?.status === 200) {
        const records = Array.isArray(response.data.allData)
          ? response.data.allData
          : [];

        setFaqData(records);
        setCurrentPage(1);
        setActiveIndex(null);
      } else {
        setLoadError(true);
        console.error(
          "Unable to load FAQs:",
          response?.data?.validation_error
        );
      }
    } catch (error) {
      console.error("Error fetching FAQ data:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqData();
    setActiveIndex(null);
  }, [fetchFaqData, section, id]);

  const totalPages = Math.ceil(faqData.length / FAQS_PER_PAGE);

  const startIndex = (currentPage - 1) * FAQS_PER_PAGE;

  const currentFAQs = faqData.slice(
    startIndex,
    startIndex + FAQS_PER_PAGE
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);
    setActiveIndex(null);
  };

  const toggleFAQ = (index) => {
    setActiveIndex((previous) =>
      previous === index ? null : index
    );
  };

  if (!loading && !loadError && faqData.length === 0) {
    return null;
  }

  return (
    <section id="faq" className="fsq-section section">
      <div className="container">
        <header className="fsq-heading">
          <span className="fsq-eyebrow">
            HELP &amp; INFORMATION
          </span>

          <h2>Frequently Asked Questions</h2>

          <span className="fsq-heading-accent" />
        </header>

        <div className="fsq-content">
          {loading && (
            <div className="fsq-message" role="status">
              <span
                className="fsq-spinner"
                aria-hidden="true"
              />
              Loading frequently asked questions...
            </div>
          )}

          {loadError && (
            <div
              className="fsq-message fsq-error"
              role="alert"
            >
              <p>
                We could not load the frequently asked questions.
                Please try again.
              </p>

              <button
                type="button"
                className="fsq-retry"
                onClick={fetchFaqData}
              >
                <i
                  className="bi bi-arrow-clockwise"
                  aria-hidden="true"
                />
                Try Again
              </button>
            </div>
          )}

          {!loading && !loadError && faqData.length > 0 && (
            <>
              <div className="fsq-list">
                {currentFAQs.map((item, index) => {
                  const isActive = activeIndex === index;
                  const questionNumber = startIndex + index + 1;
                  const recordKey = item.id ?? questionNumber;

                  const answerId = `faq-answer-${recordKey}`;
                  const questionId = `faq-question-${recordKey}`;

                  return (
                    <article
                      className={`fsq-item ${
                        isActive ? "fsq-active" : ""
                      }`}
                      key={recordKey}
                    >
                      <button
                        type="button"
                        id={questionId}
                        className="fsq-question"
                        aria-expanded={isActive}
                        aria-controls={answerId}
                        onClick={() => toggleFAQ(index)}
                      >
                        <span className="fsq-number">
                          {String(questionNumber).padStart(2, "0")}
                        </span>

                        <span className="fsq-question-text">
                          {item.faq}
                        </span>

                        <span
                          className="fsq-toggle"
                          aria-hidden="true"
                        >
                          <i
                            className={`bi ${
                              isActive
                                ? "bi-dash-lg"
                                : "bi-plus-lg"
                            }`}
                          />
                        </span>
                      </button>

                      <div
                        id={answerId}
                        className="fsq-answer"
                        role="region"
                        aria-labelledby={questionId}
                        hidden={!isActive}
                      >
                        <div className="fsq-answer-inner">
                          <HTMLDisplay
                            html={item.faqAns || ""}
                            textNo={100}
                            type="title"
                          />
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <nav
                  className="fsq-pagination"
                  aria-label="FAQ pages"
                >
                  <p className="fsq-pagination-info" aria-live="polite">
                    Showing {startIndex + 1}–
                    {Math.min(
                      startIndex + FAQS_PER_PAGE,
                      faqData.length
                    )}{" "}
                    of {faqData.length} questions
                  </p>

                  <div className="fsq-pagination-controls">
                    <button
                      type="button"
                      className="fsq-page-button fsq-page-previous"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i
                        className="bi bi-chevron-left"
                        aria-hidden="true"
                      />
                      <span>Previous</span>
                    </button>

                    {Array.from(
                      { length: totalPages },
                      (_, index) => index + 1
                    ).map((page) => (
                      <button
                        type="button"
                        key={page}
                        className={`fsq-page-button ${
                          currentPage === page
                            ? "fsq-page-active"
                            : ""
                        }`}
                        onClick={() => goToPage(page)}
                        aria-label={`Page ${page}`}
                        aria-current={
                          currentPage === page ? "page" : undefined
                        }
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      type="button"
                      className="fsq-page-button fsq-page-next"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <span>Next</span>
                      <i
                        className="bi bi-chevron-right"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </nav>
              )}
            </>
          )}
        </div>

        <div className="fsq-footer">
          <span
            className="fsq-footer-icon"
            aria-hidden="true"
          >
            <i className="bi bi-info-circle" />
          </span>

          <p>
            Need more information? Please contact the Ministry
            through the official contact channels.
          </p>
        </div>
      </div>
    </section>
  );
}

export default React.memo(FaqSection);
